import * as XLSX from 'xlsx';
import type { ColumnDef } from './schema';

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function escapeCsv(v: unknown): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// Build a recognizable filename like: BOX_2026-08-18_14-30-25_8472.csv
export function buildFileName(prefix: string, ext: string): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const time = `${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `${prefix}_${date}_${time}_${rand}.${ext}`;
}

export function exportCSV(filename: string, headers: string[], rows: Record<string, unknown>[]) {
  const lines = [headers.join(',')];
  rows.forEach((r) => lines.push(headers.map((h) => escapeCsv(r[h])).join(',')));
  // UTF-8 BOM so Excel opens Chinese / special characters correctly
  downloadBlob('\uFEFF' + lines.join('\r\n'), filename, 'text/csv;charset=utf-8;');
}

export function exportExcel(filename: string, headers: string[], rows: Record<string, unknown>[]) {
  const data = rows.map((r) => {
    const o: Record<string, unknown> = {};
    headers.forEach((h) => { o[h] = r[h] ?? ''; });
    return o;
  });
  const ws = XLSX.utils.json_to_sheet(data, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
}

export function exportTemplateCSV(filename: string, cols: ColumnDef[], example?: Record<string, string>) {
  const headers = cols.map((c) => c.label);
  const rows: Record<string, unknown>[] = [];
  if (example) {
    const r: Record<string, unknown> = {};
    cols.forEach((c) => { r[c.label] = example[c.key] ?? ''; });
    rows.push(r);
  }
  exportCSV(filename, headers, rows);
}

export interface ParsedSheet {
  headers: string[];
  rows: Record<string, unknown>[];
}

// Convert an Excel date serial number (days since 1899-12-30) to a YYYY-MM-DD
// string. Timezone-safe (uses UTC). Returns null when the value isn't a valid serial.
export function excelSerialToISODate(serial: number): string | null {
  const days = Math.floor(serial);
  if (!Number.isFinite(days) || days <= 0) return null;
  const d = new Date(Date.UTC(1899, 11, 30) + days * 86400000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

// Normalize a raw cell value for a date field into a clean YYYY-MM-DD string.
// Handles Excel serial numbers (as numbers or strings), Date objects, and common
// date strings like "2025-01-17", "01/17/2025" or "01-17-25". Returns null if unparseable.
export function normalizeDateString(v: unknown): string | null {
  if (v == null) return null;

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
  }

  if (typeof v === 'number' && Number.isFinite(v) && v > 59) {
    return excelSerialToISODate(v);
  }

  const s = String(v).trim();
  if (!s) return null;

  // Excel serial number as a string, e.g. "45674.000497685185" or "45674"
  if (/^\d{4,6}(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (n > 59) return excelSerialToISODate(n);
  }

  const m = s.match(/^(\d{1,4})[-\/.](\d{1,2})[-\/.](\d{1,4})/);
  if (m) {
    const a = m[1];
    const b = m[2].padStart(2, '0');
    const c = m[3];
    if (a.length === 4) return `${a}-${b}-${c.padStart(2, '0')}`;
    const yy = c.length === 2 ? `20${c}` : c;
    return `${yy}-${a.padStart(2, '0')}-${b}`;
  }

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  return null;
}

export async function parseSheet(file: File): Promise<ParsedSheet> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: false });

  const headers: string[] = [];
  if (ws['!ref']) {
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r: range.s.r, c });
      const cell = ws[addr];
      if (cell && cell.v != null && String(cell.v).trim() !== '') {
        headers.push(String(cell.v).trim());
      }
    }
  }
  return { headers, rows };
}